import React from "react";
import { Col, Container, Row, Card, Spinner } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faKey,
    faLink,
    faFileExcel,
    faCode,
    faBell,
    faDownload,
    faCheckCircle,
    faExclamationCircle,
    faArrowRight,
    faServer,
    faExternalLinkAlt
} from "@fortawesome/free-solid-svg-icons";
import { getRequest } from "../../../../Helper/ApiClient";

// Status Card Component
interface StatusCardProps {
    icon: any;
    title: string;
    status: "success" | "warning" | "inactive" | "loading";
    statusText: string;
    detail?: string;
    linkTo?: string;
    linkText?: string;
}

const StatusCard: React.FC<StatusCardProps> = ({
    icon, title, status, statusText, detail, linkTo, linkText
}) => {
    const statusColors = {
        success: { bg: "#e8f5e9", border: "#4caf50", icon: "#4caf50" },
        warning: { bg: "#fff3e0", border: "#ff9800", icon: "#ff9800" },
        inactive: { bg: "#f5f5f5", border: "#9e9e9e", icon: "#9e9e9e" },
        loading: { bg: "#f5f5f5", border: "#9e9e9e", icon: "#9e9e9e" },
    };

    const colors = statusColors[status];

    return (
        <Card
            className="h-100 border-0"
            style={{
                backgroundColor: colors.bg,
                borderLeft: `4px solid ${colors.border}`,
                borderRadius: 8
            }}
        >
            <Card.Body className="d-flex align-items-start gap-3 py-3">
                <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                        width: 40,
                        height: 40,
                        backgroundColor: "white",
                        color: colors.icon
                    }}
                >
                    {status === "loading" ? (
                        <Spinner animation="border" size="sm" />
                    ) : (
                        <FontAwesomeIcon icon={icon} />
                    )}
                </div>
                <div className="flex-grow-1 min-w-0">
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <span className="fw-semibold">{title}</span>
                        {status === "success" && (
                            <FontAwesomeIcon icon={faCheckCircle} className="text-success" size="sm" />
                        )}
                        {status === "warning" && (
                            <FontAwesomeIcon icon={faExclamationCircle} className="text-warning" size="sm" />
                        )}
                    </div>
                    <div style={{ color: "#666" }} className="text-12">{statusText}</div>
                    {detail && (
                        <code
                            className="d-block text-truncate mt-1 text-10"
                            style={{ maxWidth: "100%" }}
                            title={detail}
                        >
                            {detail}
                        </code>
                    )}
                    {linkTo && linkText && (
                        <NavLink
                            to={linkTo}
                            className="text-decoration-none d-inline-flex align-items-center gap-1 mt-2 text-12"
                        >
                            {linkText} <FontAwesomeIcon icon={faArrowRight} size="xs" />
                        </NavLink>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

// Feature Link Component
interface FeatureLinkProps {
    icon: any;
    title: string;
    description: string;
    to?: string;
    href?: string;
    external?: boolean;
}

const FeatureLink: React.FC<FeatureLinkProps> = ({ icon, title, description, to, href, external }) => {
    const content = (
        <div className="d-flex align-items-center gap-3 p-3 rounded-3 h-100"
             style={{ backgroundColor: "#f8f9fa", transition: "background-color 0.2s" }}
             onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#e9ecef"}
             onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "#f8f9fa"}
        >
            <div
                className="rounded d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: 44, height: 44, backgroundColor: "#e3f2fd", color: "#1976d2" }}
            >
                <FontAwesomeIcon icon={icon} size="lg" />
            </div>
            <div className="flex-grow-1">
                <div className="fw-semibold d-flex align-items-center gap-2">
                    {title}
                    {external && <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" className="text-muted" />}
                </div>
                <div style={{ color: "#666" }} className="text-12">{description}</div>
            </div>
            <FontAwesomeIcon icon={faArrowRight} className="text-muted" />
        </div>
    );

    if (href) {
        return (
            <a href={href} className="text-decoration-none text-dark" target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined}>
                {content}
            </a>
        );
    }

    return (
        <NavLink to={to || "#"} className="text-decoration-none text-dark">
            {content}
        </NavLink>
    );
};

export default function Index() {
    const apiSettingsRequest = getRequest('/api/api_settings');
    const skuMappingsRequest = getRequest('/api/user/productReferences/count');

    const hasApiKey = apiSettingsRequest.isSuccess && apiSettingsRequest.data?.api_key?.length > 0;
    const apiKey = apiSettingsRequest.data?.api_key || null;
    const stockEndpoint = apiSettingsRequest.data?.stock_endpoint_url || null;

    const skuCount = skuMappingsRequest.data?.count ?? 0;

    return (
        <Container className="py-4">
            {/* Header */}
            <Row className="mb-4">
                <Col>
                    <h1 className="mb-2">API Integration</h1>
                    <p className="text-muted mb-0">
                        Connect your systems with NaskorSports. Synchronize product data,
                        receive live stock updates, and streamline your ordering process.
                    </p>
                </Col>
            </Row>

            {/* Status Dashboard */}
            <Row className="mb-4">
                <Col>
                    <h2 className="text-muted mb-3 type-eyebrow">
                        Your Integration Status
                    </h2>
                </Col>
            </Row>

            <Row className="g-3 mb-5">
                <Col md={4}>
                    <StatusCard
                        icon={faKey}
                        title="API Key"
                        status={apiSettingsRequest.isPending ? "loading" : hasApiKey ? "success" : "warning"}
                        statusText={
                            apiSettingsRequest.isPending
                                ? "Loading..."
                                : hasApiKey
                                    ? "Active"
                                    : "Not configured"
                        }
                        detail={hasApiKey ? `${apiKey?.substring(0, 8)}...` : undefined}
                        linkTo="/service-center/api/key"
                        linkText={hasApiKey ? "Manage" : "Generate Key"}
                    />
                </Col>
                <Col md={4}>
                    <StatusCard
                        icon={faLink}
                        title="SKU Mappings"
                        status={skuMappingsRequest.isPending ? "loading" : skuCount > 0 ? "success" : "warning"}
                        statusText={
                            skuMappingsRequest.isPending
                                ? "Loading..."
                                : skuCount > 0
                                    ? `${skuCount} products mapped`
                                    : "No mappings yet"
                        }
                        linkTo="/service-center/api/sku-mapping"
                        linkText={skuCount > 0 ? "Manage Mappings" : "Start Mapping"}
                    />
                </Col>
                <Col md={4}>
                    <StatusCard
                        icon={faServer}
                        title="Stock Endpoint"
                        status={apiSettingsRequest.isPending ? "loading" : stockEndpoint ? "success" : "inactive"}
                        statusText={
                            apiSettingsRequest.isPending
                                ? "Loading..."
                                : stockEndpoint
                                    ? "Configured"
                                    : "Not configured"
                        }
                        detail={stockEndpoint || undefined}
                        linkTo="/service-center/api/stock"
                        linkText={stockEndpoint ? "Edit Endpoint" : "Configure"}
                    />
                </Col>
            </Row>

            {/* Features Section */}
            <Row className="mb-4">
                <Col>
                    <h2 className="text-muted mb-3 type-eyebrow">
                        Available Features
                    </h2>
                </Col>
            </Row>

            <Row className="g-3 mb-5">
                <Col md={6}>
                    <FeatureLink
                        icon={faFileExcel}
                        title="Basket with Excel Upload"
                        description="Go to your basket and import products via Excel using your SKUs"
                        to="/basket"
                    />
                </Col>
                <Col md={6}>
                    <FeatureLink
                        icon={faCode}
                        title="REST API"
                        description="Programmatic access to product data"
                        to="/service-center/api/rest"
                    />
                </Col>
                <Col md={6}>
                    <FeatureLink
                        icon={faBell}
                        title="Live Stock Updates"
                        description="Receive real-time inventory notifications"
                        to="/service-center/api/stock"
                    />
                </Col>
                <Col md={6}>
                    <FeatureLink
                        icon={faDownload}
                        title="CSV Export"
                        description="Download product master data, pricing, and active promotions"
                        href="/service-center/api/csv/download"
                    />
                </Col>
            </Row>

        </Container>
    );
}
